import mParticle, { SDKEventAttrs } from '@mparticle/web-sdk';
import '@mparticle/web-google-analytics-4-client-kit';
import { EpgChannel, LiveEventStream, Media, Video } from 'types/api/media';
import {
  getCurrentEpgProgram,
  getSeason,
  isLiveEvent,
  isEpgChannel,
  isExtra,
  isMovie,
} from 'support/contentUtils';
import { getPageId, PageId } from 'types/pageId';
import { ErrorType } from 'types/analytics';
import {
  MParticleInteractiveAdEvents,
  MParticleVideoEvents,
  VideoPayload,
} from 'types/analytics/mParticle';
import { ListItemContext } from 'types/events';
import { getSourceSite } from 'support/mParticleUtils';
import {
  convertSecondsToTimeString,
  convertTimeStringToSeconds,
} from 'support/timeUtils';
import { InteractiveAdType } from 'components/pages/playback/interactive-ads/InteractiveAdUtils';

export default class MParticleVideo {
  private payload: VideoPayload = {};
  private ccEnabled = false;
  private hasReportedFinish = false;
  private videoSessionId = '-1';

  reportStart(currentPosition: number) {
    this.hasReportedFinish = false;

    const modifiedPayload = {
      ...this.payload,
      External_Session_ID: this.videoSessionId,
    };

    this.trackPlaybackEvent(MParticleVideoEvents.START, {
      currentPosition,
      payload: modifiedPayload,
    });
  }

  reportMidPoint(currentPosition: number) {
    this.trackPlaybackEvent(MParticleVideoEvents.MIDPOINT, {
      currentPosition,
      payload: this.payload,
    });
  }

  reportLiveEventEnd(currentPosition: number, payloadOverride?: VideoPayload) {
    this.reportFinish(currentPosition, true, payloadOverride);
  }

  reportCreditsReached(currentPosition: number, isFirstCredits: boolean) {
    if (!isFirstCredits) return;

    this.reportFinish(currentPosition, true);
  }

  reportEnd(currentPosition: number, hasCompleted: boolean) {
    this.reportFinish(currentPosition, hasCompleted);
  }

  reportPlaybackError(errorType: ErrorType, message: string) {
    const payload = {
      Error_Type: errorType,
      Error_Message: message,
      Content_ID: this.payload.Content_ID ?? '',
      Source_Page: this.payload.Source_Page ?? '',
      Episode_Name: this.payload.Episode_Name ?? '',
      Series_Name: this.payload.Series_Name ?? '',
      Source_Site: getSourceSite(),
    };

    mParticle.logEvent(
      MParticleVideoEvents.ERROR,
      mParticle.EventType.Other,
      payload,
    );
  }

  setContentPayload(
    content: Video | LiveEventStream | EpgChannel,
    fromPage: PageId | null,
    videoSessionId: string,
    listItemContext?: ListItemContext,
  ) {
    const payload: VideoPayload = this.createPayload(content);

    payload['Source_Page'] = fromPage ? getPageId(fromPage) : '';

    if (listItemContext) {
      listItemContext.itemIndex += 1;
      listItemContext.rowIndex += 1;
      payload.listItemContext = listItemContext;
    }

    this.payload = payload;
    this.videoSessionId = videoSessionId;
  }

  setClosedCaptionPayload(ccEnabled: boolean) {
    this.ccEnabled = ccEnabled;
  }

  reportPlayRecommendation(type: 'clicked' | 'autoPlay', content: Media) {
    const payload = this.createPayload(content);

    const modifiedPayload = {
      Genre: payload.Genre,
      Season_Number: payload.Season_Number || null,
      Episode_Number: payload.Episode_Number,
      Episode_Name: payload.Episode_Name,
      Series_Name: payload.Series_Name,
      Source_Page: this.payload.Source_Page,
      Source_Site: payload.Source_Site,
      ...(type === 'autoPlay'
        ? {
            Content_ID: payload.Content_ID,
            Content_Format: payload.Content_Format,
          }
        : {}),
    };

    mParticle.logEvent(
      type === 'clicked'
        ? MParticleVideoEvents.RECOMMENDATION_CLICKED
        : MParticleVideoEvents.RECOMMENDATION_AUTO_PLAY,
      mParticle.EventType.Other,
      modifiedPayload,
    );
  }

  getMParticlePayload() {
    return this.payload;
  }

  /* =============================
   * Interactive Ad Reporting
   * ============================= */

  reportInteractiveAdExit(exitType: string, adType?: InteractiveAdType) {
    const payload = {
      Source_Site: getSourceSite(),
      Ad_Type: adType,
      Exit_Type: exitType,
    };

    mParticle.logEvent(
      MParticleInteractiveAdEvents.AD_EXIT,
      mParticle.EventType.Other,
      payload,
    );
  }

  // Ad slot events only apply to TrueX ads
  reportInteractiveAdSlotStart(adType?: InteractiveAdType) {
    if (adType !== 'TrueX') return;
    const payload = {
      Source_Site: getSourceSite(),
    };

    mParticle.logEvent(
      MParticleInteractiveAdEvents.AD_SLOT_START,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportInteractiveAdSlotComplete(adType?: InteractiveAdType) {
    if (adType !== 'TrueX') return;
    const payload = {
      Source_Site: getSourceSite(),
    };

    mParticle.logEvent(
      MParticleInteractiveAdEvents.AD_SLOT_COMPLETE,
      mParticle.EventType.Other,
      payload,
    );
  }

  private reportFinish(
    currentPosition: number,
    hasCompleted: boolean,
    payloadOverride?: VideoPayload,
  ) {
    if (this.hasReportedFinish) return;

    this.trackPlaybackEvent(MParticleVideoEvents.FINISH, {
      currentPosition,
      hasCompleted,
      payload: payloadOverride ?? this.payload,
    });

    this.hasReportedFinish = true;
  }

  private createPayload(content: Media): VideoPayload {
    let payload: VideoPayload;

    if (isEpgChannel(content)) {
      payload = this.parseEpgProgramContent(content as EpgChannel);
    } else if (isLiveEvent(content)) {
      payload = this.parseLiveContent(content as LiveEventStream);
    } else {
      payload = this.parseVideoContent(content as Video);
    }

    return { ...payload, Source_Site: getSourceSite() };
  }

  private parseVideoContent(content: Video): VideoPayload {
    return {
      Content_ID: content.guid || content.id,
      Episode_Name: content.title,
      Episode_Number: content.episode || null,
      Genre:
        content.genre ||
        content.imdbGenre ||
        (content as Media).genres?.join(', ') ||
        '',
      Season_Number: getSeason(content) || null,
      Series_Name: content.seriesName || (content as Media).title,
      Source_Site: getSourceSite(),
      Content_Format: isExtra(content)
        ? 'Trailer'
        : isMovie(content)
        ? 'Movies'
        : 'Series',
    };
  }

  private parseLiveContent(content: LiveEventStream): VideoPayload {
    return {
      Content_ID: content.analytics.guid ?? '',
      Episode_Name: content.title,
      Episode_Number: null,
      Genre: content.analytics.comscoreGenre ?? '',
      Series_Name: content.analytics.title,
      Season_Number: null,
      Source_Site: getSourceSite(),
      Content_Format: 'Live Event',
    };
  }

  private parseEpgProgramContent(channel: EpgChannel): VideoPayload {
    const epgProgram = getCurrentEpgProgram(channel);

    return {
      Content_ID: epgProgram?.assetId,
      Episode_Name: epgProgram?.subtitle ?? '',
      Episode_Number: epgProgram?.episode || null,
      Genre: epgProgram?.genre ?? channel.genre ?? '',
      Series_Name: epgProgram?.title ?? '',
      Season_Number: epgProgram?.season || null,
      Source_Site: getSourceSite(),
      Content_Format: 'EPG Channel',
    };
  }

  private getElapsedAndRemainingTime(
    currentPosition: number,
    hasCompleted = false,
  ) {
    const positionInSecs = Math.round(currentPosition / 1000);
    const durationInSecs = this.payload.Length
      ? convertTimeStringToSeconds(this.payload.Length)
      : 0;

    const elapsed = hasCompleted
      ? this.payload.Length
      : convertSecondsToTimeString(positionInSecs);
    const remaining = convertSecondsToTimeString(
      hasCompleted ? 0 : durationInSecs - positionInSecs,
    );

    if (durationInSecs > 0) {
      return { elapsed, remaining };
    }

    return null;
  }

  private trackPlaybackEvent(
    event: string,
    data: {
      currentPosition?: number;
      payload?: VideoPayload;
      hasCompleted?: boolean;
    },
  ) {
    const { currentPosition, payload, hasCompleted } = data;
    let modifiedPayload = payload ?? this.payload;

    if (currentPosition !== undefined) {
      modifiedPayload = {
        ...modifiedPayload,
        Closed_Caption: this.ccEnabled ? 'Yes' : 'No',
        Content_Format: payload?.Content_Format ?? '',
      };
    }

    mParticle.logEvent(
      event,
      mParticle.EventType.Other,
      modifiedPayload as SDKEventAttrs,
    );
  }
}
